'use client'

import { MODAL_TYPE, Modal, type ModalAPI } from 'fukku/Modal'
import { useLabels } from 'labels/hooks/useLabels/useLabels'
import dynamic from 'next/dynamic'
import { useSizeGuide } from 'pdp/src/contexts/SizeGuideProvider/SizeGuideProvider'
import { useSizes } from 'pdp/src/contexts/SizesProvider/SizesProvider'
import type { FullProduct } from 'pdp/src/types/FullProduct'
import { useCallback, useEffect, useRef } from 'react'
import { useResponsive } from 'responsive/hooks/useResponsive/useResponsive'
import { cx } from 'utils/cx'

import text from 'fukku/styles/texts.module.scss'
import styles from './SizeGuideModal.module.scss'

const DynamicSizeGuideClient = dynamic(
	() =>
		import('size-guide/components/SizeGuide/client/SizeGuideClient').then(
			(component) => component.SizeGuideClient
		),
	{
		loading: () => (
			<div className={styles.loading} data-testid='size-guide-loading' />
		),
	}
)

interface SizeGuideModalProps {
	readonly product: FullProduct
}

export function SizeGuideModal({
	product,
}: SizeGuideModalProps): React.ReactNode {
	const { t } = useLabels()

	const modalRef = useRef<ModalAPI>(null)
	const { isLessThanLarge } = useResponsive()
	const { isSizeGuideOpen, setIsSizeGuideOpen } = useSizeGuide()
	const { openSheet } = useSizes()

	const familyIds = product.families.map((family) => family.id).join(',')

	const onSizeGuideClose = useCallback((): void => {
		setIsSizeGuideOpen(false)
		if (isLessThanLarge) {
			openSheet(true)
		}
	}, [isLessThanLarge])

	useEffect(() => {
		if (isSizeGuideOpen) {
			modalRef.current?.show()
		}
	}, [isSizeGuideOpen])

	return (
		<Modal
			name='size-guide-modal'
			title={t('sizeGuide.link')}
			onClose={onSizeGuideClose}
			type={MODAL_TYPE.XL}
			ref={modalRef}
		>
			<h1 className={cx(styles.title, text.titleL)}>{t('sizeGuide.link')}</h1>
			<DynamicSizeGuideClient
				searchParams={{ line: product.genderId, familyIds }}
				isModal
			/>
		</Modal>
	)
}
